package one.healthium.today.ui.med

import androidx.compose.foundation.Image
import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.statusBarsPadding
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.relocation.BringIntoViewRequester
import androidx.compose.foundation.relocation.bringIntoViewRequester
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.verticalScroll
import androidx.compose.material3.LocalContentColor
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.CompositionLocalProvider
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.remember
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.platform.testTag
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.em
import androidx.compose.ui.util.fastForEach
import loco.MED_TODAY_STEPS_FORMAT
import loco.TODAY_LOGGED_SYMPTOMS_LABEL
import loco.TODAY_LOG_CONTROLS_SECTION
import loco.TODAY_LOG_SYMPTOMS_LABEL
import loco.TODAY_LOG_WATER_BUTTON
import loco.TODAY_LOG_WEIGHT_BUTTON
import loco.TODAY_MEALS_SECTION
import loco.TODAY_MEAL_PLAN_SECTION
import one.healthium.base.utils.Formatters.formatCalories
import one.healthium.base.utils.Formatters.formatWeight
import one.healthium.base.utils.toVolumeString
import one.healthium.base.utils.toVolumeUnit
import one.healthium.domain.user.models.FoodItemModel
import one.healthium.healthKit.ui.HealthKitConnectBanner
import one.healthium.healthKit.ui.HealthKitConnectBannerContract
import one.healthium.health_advice.ui.message.HealthAdviceMessage
import one.healthium.loco.resolve
import one.healthium.meal_plans.domain.MealPlanInfoModel
import one.healthium.rateus.data.ShowAppRateForStart
import one.healthium.today.ui.TodayUiState
import one.healthium.today.ui.TodayViewModel
import one.healthium.today.ui.components.TodayUserProfile
import one.healthium.today.ui.models.DailyStepsUiModel
import one.healthium.ui.common.message.AlertUiMessageHandler
import one.healthium.ui.compose.components.AppIcons
import one.healthium.ui.compose.components.AppScaffold
import one.healthium.ui.compose.platform.PlatformColorsEffect
import one.healthium.ui.compose.theme.AppTheme
import one.healthium.ui.compose.theme.Themes
import one.healthium.ui.compose.theme.black10
import one.healthium.ui.compose.theme.white
import org.jetbrains.compose.resources.painterResource
import org.jetbrains.compose.resources.stringResource
import androidx.compose.ui.tooling.preview.Preview
import org.koin.compose.viewmodel.koinViewModel

interface TodayEventDelegate {
    fun onMealPlanClicked(id: String)
    fun onLogFoodClicked()
    fun onLogWaterClicked()
    fun onLogWeightClicked()
    fun onLogSymptomsClicked()
}

@Composable
fun TodayScreen(
    eventDelegate: TodayEventDelegate
) {
    PlatformColorsEffect(lightStatusBar = true)

    val viewModel = koinViewModel<TodayViewModel>()
    val state by viewModel.state().collectAsState()

    ShowAppRateForStart()

    TodayScreenContent(
        state = state,
        onLogWaterClicked = eventDelegate::onLogWaterClicked,
        onLogWeightClicked = eventDelegate::onLogWeightClicked,
        onMealPlanClicked = { eventDelegate.onMealPlanClicked(it.id) },
        onAddFoodClicked = eventDelegate::onLogFoodClicked,
        onLogSymptomsClicked = eventDelegate::onLogSymptomsClicked,
        healthKitContract = viewModel,
    )

    AlertUiMessageHandler(viewModel.uiMessages)
}

@Composable
internal fun TodayScreenContent(
    state: TodayUiState,
    onLogWaterClicked: () -> Unit,
    onLogWeightClicked: () -> Unit,
    onMealPlanClicked: (MealPlanInfoModel) -> Unit,
    onAddFoodClicked: () -> Unit,
    onLogSymptomsClicked: () -> Unit,
    healthKitContract: HealthKitConnectBannerContract,
) {
    AppScaffold(Modifier.testTag("today_screen")) {
        Column(
            modifier = Modifier.fillMaxSize().verticalScroll(rememberScrollState()),
        ) {
            CaloriesCounter(state, onAddFoodClicked, onLogSymptomsClicked)
            if (state.shouldShowHKBanner) {
                Spacer(Modifier.height(24.dp))
                HealthKitConnectBanner(contract = healthKitContract)
                Spacer(Modifier.height(24.dp))
            }
            MealPlanSection(state, onMealPlanClicked)
            DailyControlsSection(state, onLogWeightClicked, onLogWaterClicked, onLogSymptomsClicked)
            MealsSection(state)
            Spacer(Modifier.height(80.dp))
        }
    }
}

@Composable
private fun CaloriesCounter(
    state: TodayUiState,
    onAddFoodClicked: () -> Unit,
    onLogSymptomsClicked: () -> Unit,
) {
    val bringIntoViewRequester = remember { BringIntoViewRequester() }

    Box {
        Box(
            modifier = Modifier
                .fillMaxWidth()
                .height(380.dp)
                .background(AppTheme.colors.primary)
        )

        Column(
            modifier = Modifier
                .bringIntoViewRequester(bringIntoViewRequester)
                .fillMaxWidth()
                .statusBarsPadding(),
        ) {
            val userProfile = state.userProfile
            if (userProfile != null) {
                CompositionLocalProvider(LocalContentColor provides white) {
                    TodayUserProfile(
                        modifier = Modifier
                            .fillMaxWidth()
                            .padding(horizontal = 24.dp)
                            .padding(top = 12.dp),
                        userProfile = userProfile,
                    )
                }
            }
            Spacer(Modifier.height(4.dp))
            HealthAdviceMessage(
                modifier = Modifier.fillMaxWidth(),
                bringIntoViewRequester = bringIntoViewRequester,
                onLogSymptomsClicked = onLogSymptomsClicked,
                style = AppTheme.textStyles.secondary_17_400,
                lineHeight = 1.5.em,
                textAlign = TextAlign.Start,
            )
            Spacer(Modifier.height(10.dp))
            CaloriesCounter(
                modifier = Modifier
                    .fillMaxWidth()
                    .padding(horizontal = 24.dp),
                model = state.todayCaloriesCounter,
                onAddFoodClicked = onAddFoodClicked,
            )
        }
    }
}

@Composable
private fun MealPlanSection(
    state: TodayUiState,
    onMealPlanClicked: (MealPlanInfoModel) -> Unit
) {
    val mealPlan = state.mealPlan
    if (mealPlan != null) {
        Section(stringResource(loco.Res.string.TODAY_MEAL_PLAN_SECTION))
        MealPlanItem(
            modifier = Modifier.padding(horizontal = 12.dp),
            mealPlan = mealPlan,
            onMealPlanClicked = onMealPlanClicked,
        )
    }
}

@Composable
private fun MealsSection(state: TodayUiState) {
    val meals = state.meals.take(3)
    if (meals.isNotEmpty()) {
        Section(stringResource(loco.Res.string.TODAY_MEALS_SECTION))
        meals.fastForEach {
            FoodItem(
                modifier = Modifier.padding(horizontal = 12.dp).padding(bottom = 12.dp),
                model = it,
            )
        }
    }
}

@Composable
private fun DailyControlsSection(
    state: TodayUiState,
    onLogWeightClicked: () -> Unit,
    onLogWaterClicked: () -> Unit,
    onLogSymptomsClicked: () -> Unit,
) {
    Section(stringResource(loco.Res.string.TODAY_LOG_CONTROLS_SECTION))
    LogWeightItem(
        state = state,
        modifier = Modifier
            .padding(horizontal = 12.dp)
            .padding(bottom = 12.dp),
        onLogWeightClicked = onLogWeightClicked,
    )
    LogWaterItem(
        state = state,
        modifier = Modifier
            .padding(horizontal = 12.dp)
            .padding(bottom = 12.dp),
        onWaterLogClicked = onLogWaterClicked,
    )
    LogSymptomsItem(
        state = state,
        modifier = Modifier
            .padding(horizontal = 12.dp)
            .padding(bottom = 12.dp),
        onClick = onLogSymptomsClicked,
    )
    state.todayStepsCounter?.let {
        StepsCounterItem(
            modifier = Modifier
                .padding(horizontal = 12.dp)
                .padding(bottom = 12.dp),
            steps = it,
        )
    }
}

@Composable
private fun Section(text: String) {
    Text(
        modifier = Modifier.padding(horizontal = 16.dp, vertical = 12.dp),
        text = text,
        style = AppTheme.textStyles.secondary_17_500,
        color = AppTheme.colors.text400,
    )
}

@Composable
private fun MealPlanItem(
    modifier: Modifier = Modifier,
    mealPlan: MealPlanInfoModel,
    onMealPlanClicked: (MealPlanInfoModel) -> Unit,
) {
    Row(
        modifier = modifier
            .clip(AppTheme.shapes.medium)
            .clickable {
                onMealPlanClicked(mealPlan)
            }
            .fillMaxWidth()
            .background(AppTheme.colors.surface, AppTheme.shapes.medium)
            .padding(horizontal = 12.dp, vertical = 16.dp),
        verticalAlignment = Alignment.CenterVertically,
    ) {
        Box(
            modifier = Modifier.size(36.dp).background(black10, CircleShape),
            contentAlignment = Alignment.Center,
        ) {
            Text(
                text = "🐟",
                style = AppTheme.textStyles.secondary_17_500,
            )
        }
        Spacer(Modifier.width(12.dp))
        Text(
            modifier = Modifier.weight(1f),
            text = mealPlan.name.resolve(),
            style = AppTheme.textStyles.secondary_17_500,
            color = AppTheme.colors.text100,
        )
        Spacer(Modifier.width(8.dp))
        Image(
            painter = painterResource(AppIcons.icArrowRight),
            contentDescription = null,
        )
    }
}

@Composable
private fun FoodItem(
    modifier: Modifier = Modifier,
    model: FoodItemModel,
) {
    Row(
        modifier = modifier
            .fillMaxWidth()
            .background(AppTheme.colors.surface, AppTheme.shapes.medium)
            .padding(horizontal = 12.dp, vertical = 13.dp),
        verticalAlignment = Alignment.CenterVertically,
    ) {
        Text(
            text = model.name,
            style = AppTheme.textStyles.secondary_16_400,
        )
        Spacer(Modifier.width(12.dp))
        Spacer(Modifier.weight(1f))
        Text(
            text = model.calories.formatCalories().resolve(),
            style = AppTheme.textStyles.secondary_16_500,
            color = AppTheme.colors.text100,
        )
    }
}

@Composable
private fun LogWeightItem(
    state: TodayUiState,
    modifier: Modifier = Modifier,
    onLogWeightClicked: () -> Unit
) {
    var weightValue: String? = null
    val latestWeightLog = state.latestWeightLog
    if (latestWeightLog != null) {
        weightValue = latestWeightLog.currentWeight.formatWeight(state.isMetric).resolve()
    }
    BaseLogItem(
        modifier = modifier,
        emoji = "✅",
        title = stringResource(loco.Res.string.TODAY_LOG_WEIGHT_BUTTON),
        value = weightValue,
        onClick = onLogWeightClicked,
    )
}

@Composable
private fun LogWaterItem(
    state: TodayUiState,
    modifier: Modifier = Modifier,
    onWaterLogClicked: () -> Unit
) {
    BaseLogItem(
        modifier = modifier,
        emoji = "💧",
        title = stringResource(loco.Res.string.TODAY_LOG_WATER_BUTTON),
        value = "${state.todayWaterIntake?.currentValue?.toVolumeString(state.isMetric)} / ${
            state.todayWaterIntake?.dailyAmount?.toVolumeString(
                state.isMetric
            )
        } ${state.isMetric.toVolumeUnit().resolve()}",
        onClick = onWaterLogClicked,
    )
}

@Composable
private fun LogSymptomsItem(
    state: TodayUiState,
    modifier: Modifier = Modifier,
    onClick: () -> Unit
) {
    BaseLogItem(
        modifier = modifier,
        emoji = "👌",
        title = stringResource(loco.Res.string.TODAY_LOG_SYMPTOMS_LABEL),
        value = "${state.todayLoggedSymptoms} ${stringResource(loco.Res.string.TODAY_LOGGED_SYMPTOMS_LABEL)}",
        onClick = onClick,
    )
}

@Composable
private fun StepsCounterItem(
    modifier: Modifier = Modifier,
    steps: DailyStepsUiModel,
) {
    BaseLogItem(
        modifier = modifier,
        emoji = "🚶‍♀️",
        title = stringResource(loco.Res.string.MED_TODAY_STEPS_FORMAT, steps.steps, steps.goal),
    )
}

@Composable
private fun BaseLogItem(
    modifier: Modifier = Modifier,
    emoji: String,
    title: String,
    value: String? = null,
    onClick: (() -> Unit)? = null,
) {
    Row(
        modifier = modifier
            .clip(AppTheme.shapes.medium)
            .then(
                if (onClick != null) Modifier.clickable { onClick() } else Modifier
            )
            .fillMaxWidth()
            .background(AppTheme.colors.surface, AppTheme.shapes.medium)
            .padding(horizontal = 12.dp, vertical = 16.dp),
        verticalAlignment = Alignment.CenterVertically,
    ) {
        Box(
            modifier = Modifier.size(36.dp).background(black10, CircleShape),
            contentAlignment = Alignment.Center,
        ) {
            Text(
                text = emoji,
                style = AppTheme.textStyles.secondary_17_500,
            )
        }
        Spacer(Modifier.width(12.dp))
        Text(
            modifier = Modifier.weight(1f),
            text = title,
            style = AppTheme.textStyles.secondary_17_500,
            color = AppTheme.colors.text100,
        )

        if (value != null) {
            Spacer(Modifier.width(8.dp))
            Text(
                text = value,
                style = AppTheme.textStyles.secondary_17_500,
                color = AppTheme.colors.text100,
            )
        }

        if (onClick != null) {
            Spacer(Modifier.width(8.dp))
            Image(
                painter = painterResource(AppIcons.icArrowRight),
                contentDescription = null,
            )
        }
    }
}

@Composable
@Preview()
private fun TodayScreenPreview() {
    AppTheme(theme = Themes.med()) {
        TodayScreenContent(
            state = TodayUiState.preview,
            onLogWaterClicked = {},
            onLogWeightClicked = {},
            onMealPlanClicked = {},
            onAddFoodClicked = {},
            onLogSymptomsClicked = {},
            healthKitContract = object : HealthKitConnectBannerContract {
                override fun onSkipClicked() = Unit
            }
        )
    }
}